import { useState } from 'react';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { CurrencyInputField, SelectField } from '@brighte/form-components';
import { Period } from '@brighte/validation';
import FinancialAlertBanner from '@components/FinancialAlertBanner/FinancialAlertBanner';
import { Columns } from '@spark-web/columns';
import { Field } from '@spark-web/field';
import { Stack } from '@spark-web/stack';
import { FinancialFieldTypes, TIME_FREQUENCY } from '@utils';

type RepaymentFieldProps<TFieldValues extends FieldValues = FieldValues> = {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;
  /**
   * Field path for the period field (e.g., 'applicant.expense.mortgage.period')
   */
  periodFieldName: FieldPath<TFieldValues>;
  /**
   * Field path for the amount field (e.g., 'applicant.expense.mortgage.amount')
   */
  amountFieldName: FieldPath<TFieldValues>;
  /**
   * Current period value - used to display the alert banner
   */
  periodValue?: Period;
  /**
   * Current amount value - used to display the alert banner
   */
  amountValue?: number;
  /**
   * Financial field type for the alert banner (MORTGAGE, RENT, etc.)
   */
  financialFieldType?: FinancialFieldTypes;
  /**
   * Whether to show the financial alert banner when values are entered
   * @default true
   */
  showAlertBanner?: boolean;
  /**
   * Whether the fields are disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Label for the amount field (if not provided, no label will be shown)
   */
  amountLabel?: string;
  /**
   * Label for the period field (if not provided, no label will be shown)
   */
  periodLabel?: string;
  /**
   * Placeholder for the period select field
   * @default 'Select'
   */
  periodPlaceholder?: string;
  /**
   * Column gap size
   * @default 'small'
   */
  gap?: 'small' | 'xxlarge';
  /**
   * Time frequency options to display in the period dropdown
   * @default TIME_FREQUENCY (all options)
   */
  periodOptions?: Array<{ label: string; value: Period }>;
  /**
   * Heading label for the field group
   */
  heading?: string;
  /**
   * Subheading/description text for the field group
   */
  subheading?: string;
};

function RepaymentField<TFieldValues extends FieldValues = FieldValues>({
  control,
  periodFieldName,
  amountFieldName,
  periodValue,
  amountValue = 0,
  financialFieldType,
  showAlertBanner = true,
  disabled = false,
  amountLabel = '',
  periodLabel = '',
  periodPlaceholder = 'Select',
  gap = 'small',
  periodOptions = TIME_FREQUENCY,
  heading,
  subheading,
}: RepaymentFieldProps<TFieldValues>) {
  const [showBanner, setShowBanner] = useState(false);

  const shouldShowBanner =
    showAlertBanner && !disabled && showBanner && periodValue && amountValue > 0 && financialFieldType;

  const content = (
    <Stack gap="large">
      <Columns gap={gap} collapseBelow="tablet">
        <SelectField
          name={periodFieldName}
          control={control}
          label={periodLabel}
          options={periodOptions}
          fieldProps={{ disabled }}
          placeholder={periodPlaceholder}
          onChange={() => {
            if (amountValue > 0) setShowBanner(true);
          }}
        />
        <CurrencyInputField
          name={amountFieldName}
          control={control}
          label={amountLabel}
          FieldProps={{ disabled }}
          onBlur={() => setShowBanner(true)}
        />
      </Columns>
      {shouldShowBanner && (
        <FinancialAlertBanner amount={amountValue} period={periodValue} financialFieldType={financialFieldType} />
      )}
    </Stack>
  );

  if (heading) {
    return (
      <Field label={heading} description={subheading}>
        {content}
      </Field>
    );
  }

  return content;
}

export default RepaymentField;
